/**
 * Firebase Phone Authentication 훅
 * Firebase Phone Auth를 통한 SMS OTP 인증 로직 관리
 *
 * @see SPEC-001-firebase-phone-auth.md
 * @see BE #129 - Firebase Authentication 구조 재정리
 */

import { useState, useCallback, useRef, useLayoutEffect } from 'react';
import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
  type ConfirmationResult,
  type Auth,
} from 'firebase/auth';
import { getFirebaseAuth, isFirebaseAuthAvailable, setAuthLanguage } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import type { Language, Locale } from '@/config/locales';
import { logger } from '@/utils/logger';

/**
 * 훅 반환 타입
 */
export interface UseFirebasePhoneAuthReturn {
  // 상태
  /** SMS 발송 중 */
  isSendingCode: boolean;
  /** OTP 검증 중 */
  isVerifying: boolean;
  /** 전체 로그인 진행 중 (SMS 발송 + 검증 + 백엔드 로그인) */
  isLoading: boolean;
  /** 에러 메시지 */
  error: string | null;
  /** 인증 코드 발송 완료 여부 */
  codeSent: boolean;
  /** Firebase 사용 가능 여부 */
  isAvailable: boolean;

  // 핸들러
  /** 인증 코드 발송 */
  sendVerificationCode: (phoneNumber: string) => Promise<boolean>;
  /** 인증 코드 확인 및 로그인 */
  confirmCode: (code: string) => Promise<boolean>;
  /** 상태 초기화 (새 전화번호로 재시도 시) */
  reset: () => void;
  /** 에러 초기화 */
  clearError: () => void;
}

/**
 * Firebase Phone Authentication 훅
 *
 * @param language 현재 언어 (에러 메시지용)
 * @param recaptchaContainerId reCAPTCHA 컨테이너 ID (기본값: 'recaptcha-container')
 * @returns 인증 상태 및 핸들러
 *
 * @example
 * ```tsx
 * const { sendVerificationCode, confirmCode, isLoading, error, codeSent } = useFirebasePhoneAuth('es');
 *
 * // 1. 인증 코드 발송
 * await sendVerificationCode('+521234567890');
 *
 * // 2. 인증 코드 확인 (자동으로 백엔드 로그인 진행)
 * const success = await confirmCode('123456');
 * if (success) {
 *   // 로그인 완료, AuthContext의 isLoggedIn이 true로 변경됨
 * }
 * ```
 */
// #522: Language(3) → Locale(15) 확장 — 에러 메시지 15언어 전수 지원
export function useFirebasePhoneAuth(
  language: Locale = 'es',
  recaptchaContainerId: string = 'recaptcha-container'
): UseFirebasePhoneAuthReturn {
  // 상태
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeSent, setCodeSent] = useState(false);

  // Refs
  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null);
  const confirmationResultRef = useRef<ConfirmationResult | null>(null);
  const authRef = useRef<Auth | null>(null);
  const generationRef = useRef(0);
  const mountedRef = useRef(true);
  const pendingSendRef = useRef<number | null>(null);
  const pendingConfirmRef = useRef<number | null>(null);

  // Auth Context
  const { firebaseLogin } = useAuth();

  // Firebase 사용 가능 여부
  const isAvailable = isFirebaseAuthAvailable();

  /**
   * 컴포넌트 언마운트 시 정리
   */
  useLayoutEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      generationRef.current += 1;
      confirmationResultRef.current = null;
      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear();
        } catch {
          // 의도적으로 에러 무시: reCAPTCHA clear 실패는 이미 정리된 상태일 수 있음
        }
        recaptchaVerifierRef.current = null;
      }
    };
  }, []);

  /**
   * reCAPTCHA Verifier 초기화
   */
  const setupRecaptcha = useCallback((generation: number): RecaptchaVerifier | null => {
    // 기존 verifier 정리
    if (recaptchaVerifierRef.current) {
      try {
        recaptchaVerifierRef.current.clear();
      } catch {
        // 의도적으로 에러 무시: reCAPTCHA clear 실패는 이미 정리된 상태일 수 있음
      }
      recaptchaVerifierRef.current = null;
    }

    // 기존 reCAPTCHA 컨테이너 정리 (이전 시도에서 남은 것)
    const existingContainer = document.getElementById(recaptchaContainerId);
    if (existingContainer) {
      existingContainer.innerHTML = '';
    }

    // Firebase Auth 인스턴스 가져오기
    const auth = getFirebaseAuth();
    if (!auth) {
      setError(getErrorMessage('FIREBASE_NOT_INITIALIZED', language));
      return null;
    }
    authRef.current = auth;

    // Auth 언어 설정 (reCAPTCHA 에러 방지)
    setAuthLanguage(language);

    // 컨테이너 요소 확인
    let container = document.getElementById(recaptchaContainerId);

    // 컨테이너가 없으면 동적 생성
    if (!container) {
      container = document.createElement('div');
      container.id = recaptchaContainerId;
      document.body.appendChild(container);
    }
    // invisible reCAPTCHA이지만 DOM에 렌더링될 수 있도록 설정
    container.style.position = 'fixed';
    container.style.bottom = '0';
    container.style.right = '0';
    container.style.zIndex = '-1';
    container.style.opacity = '0';
    container.style.pointerEvents = 'none';

    try {
      // Invisible reCAPTCHA 생성
      const verifier = new RecaptchaVerifier(auth, recaptchaContainerId, {
        size: 'invisible',
        callback: () => {
          // reCAPTCHA 검증 완료 시 호출
          logger.info('[Firebase Phone Auth] reCAPTCHA verified');
        },
        'expired-callback': () => {
          // reCAPTCHA 만료 시 호출
          logger.info('[Firebase Phone Auth] reCAPTCHA expired');
          if (mountedRef.current && generationRef.current === generation) {
            setError(getErrorMessage('RECAPTCHA_EXPIRED', language));
          }
        },
        'error-callback': () => {
          // reCAPTCHA 에러 시 호출
          logger.info('[Firebase Phone Auth] reCAPTCHA error');
        },
      });

      recaptchaVerifierRef.current = verifier;
      return verifier;
    } catch (err) {
      logger.error('[Firebase Phone Auth] Failed to setup reCAPTCHA:', err);
      setError(getErrorMessage('RECAPTCHA_SETUP_FAILED', language));
      return null;
    }
  }, [recaptchaContainerId, language]);

  /**
   * 전화번호 형식 정규화 (E.164 형식)
   * @param phone 입력된 전화번호
   * @returns E.164 형식 전화번호 (+52... 또는 +82...)
   */
  const normalizePhoneNumber = useCallback((phone: string): string => {
    // 숫자만 추출
    const digitsOnly = phone.replace(/\D/g, '');

    // 이미 국가코드가 있는 경우
    if (phone.startsWith('+')) {
      // 한국 번호 (+82): 010 → 10 (앞의 0 제거)
      if (digitsOnly.startsWith('820')) {
        return `+82${digitsOnly.slice(3)}`;
      }
      return `+${digitsOnly}`;
    }

    // 한국 번호 (82로 시작하고 그 다음이 0인 경우)
    if (digitsOnly.startsWith('820')) {
      return `+82${digitsOnly.slice(3)}`;
    }

    // 한국 번호 (82로 시작)
    if (digitsOnly.startsWith('82')) {
      return `+${digitsOnly}`;
    }

    // 한국 로컬 번호 (01x로 시작)
    if (digitsOnly.startsWith('01') && digitsOnly.length >= 10) {
      return `+82${digitsOnly.slice(1)}`;
    }

    // 멕시코 국가코드(52) 추가
    if (digitsOnly.startsWith('52')) {
      return `+${digitsOnly}`;
    }

    // 국가코드 없으면 +52 추가 (멕시코 기본)
    return `+52${digitsOnly}`;
  }, []);

  /**
   * 인증 코드 발송
   * @param phoneNumber 전화번호 (E.164 형식 권장)
   * @returns 성공 여부
   */
  const sendVerificationCode = useCallback(
    async (phoneNumber: string): Promise<boolean> => {
      if (!mountedRef.current || pendingSendRef.current === generationRef.current) return false;
      const generation = ++generationRef.current;
      const isCurrent = () => mountedRef.current && generationRef.current === generation;
      pendingSendRef.current = generation;
      confirmationResultRef.current = null;
      setIsVerifying(false);
      setIsSendingCode(true);
      setError(null);
      setCodeSent(false);

      try {
        // Firebase 사용 가능 확인
        if (!isAvailable) {
          setError(getErrorMessage('FIREBASE_NOT_INITIALIZED', language));
          return false;
        }

        // 전화번호 정규화
        const normalizedPhone = normalizePhoneNumber(phoneNumber);

        // 전화번호 유효성 검사 (멕시코: 10자리)
        const digitsOnly = normalizedPhone.replace(/\D/g, '');
        if (digitsOnly.length < 12) {
          // +52 + 10자리
          setError(getErrorMessage('INVALID_PHONE_NUMBER', language));
          return false;
        }

        // reCAPTCHA 설정
        const verifier = setupRecaptcha(generation);
        if (!verifier) {
          return false;
        }

        const auth = authRef.current;
        if (!auth) {
          setError(getErrorMessage('FIREBASE_NOT_INITIALIZED', language));
          return false;
        }

        // SMS 발송
        logger.info('[Firebase Phone Auth] Sending verification code to:', normalizedPhone);
        const confirmationResult = await signInWithPhoneNumber(auth, normalizedPhone, verifier);
        if (!isCurrent()) return false;
        confirmationResultRef.current = confirmationResult;

        setCodeSent(true);
        logger.info('[Firebase Phone Auth] Verification code sent successfully');
        return true;
      } catch (err) {
        if (!isCurrent()) return false;
        logger.error('[Firebase Phone Auth] Failed to send verification code:', err);
        const errorCode = (err as { code?: string }).code || 'UNKNOWN_ERROR';
        setError(getErrorMessage(errorCode, language));

        // reCAPTCHA 리셋
        if (recaptchaVerifierRef.current) {
          try {
            recaptchaVerifierRef.current.clear();
          } catch {
            // 의도적으로 에러 무시: reCAPTCHA clear 실패는 이미 정리된 상태일 수 있음
          }
          recaptchaVerifierRef.current = null;
        }

        return false;
      } finally {
        if (pendingSendRef.current === generation) pendingSendRef.current = null;
        if (isCurrent()) setIsSendingCode(false);
      }
    },
    [isAvailable, language, normalizePhoneNumber, setupRecaptcha]
  );

  /**
   * 인증 코드 확인 및 백엔드 로그인
   * @param code 6자리 인증 코드
   * @returns 성공 여부
   */
  const confirmCode = useCallback(
    async (code: string): Promise<boolean> => {
      const generation = generationRef.current;
      if (!mountedRef.current || pendingConfirmRef.current === generation) return false;
      const isCurrent = () => mountedRef.current && generationRef.current === generation;
      pendingConfirmRef.current = generation;
      setIsVerifying(true);
      setError(null);

      try {
        // ConfirmationResult 확인
        const confirmationResult = confirmationResultRef.current;
        if (!confirmationResult) {
          setError(getErrorMessage('NO_CONFIRMATION_RESULT', language));
          return false;
        }

        // 코드 형식 검증 (6자리 숫자)
        if (!/^\d{6}$/.test(code)) {
          setError(getErrorMessage('INVALID_CODE_FORMAT', language));
          return false;
        }

        // Firebase 인증 코드 확인
        logger.info('[Firebase Phone Auth] Confirming verification code...');
        const userCredential = await confirmationResult.confirm(code);
        if (!isCurrent()) return false;

        // ID Token 가져오기
        const idToken = await userCredential.user.getIdToken();
        if (!isCurrent()) return false;
        logger.info('[Firebase Phone Auth] Got ID token, logging in to backend...');

        // 백엔드 로그인 (AuthContext의 firebaseLogin 사용)
        const result = await firebaseLogin(idToken);
        if (!isCurrent()) return false;

        if (result.success) {
          logger.info('[Firebase Phone Auth] Login successful');
          // 상태 정리
          confirmationResultRef.current = null;
          return true;
        } else {
          logger.error('[Firebase Phone Auth] Backend login failed:', result.error);
          setError(result.error || getErrorMessage('BACKEND_LOGIN_FAILED', language));
          return false;
        }
      } catch (err) {
        if (!isCurrent()) return false;
        logger.error('[Firebase Phone Auth] Failed to confirm code:', err);
        const errorCode = (err as { code?: string }).code || 'UNKNOWN_ERROR';
        setError(getErrorMessage(errorCode, language));
        return false;
      } finally {
        if (pendingConfirmRef.current === generation) pendingConfirmRef.current = null;
        if (isCurrent()) setIsVerifying(false);
      }
    },
    [firebaseLogin, language]
  );

  /**
   * 상태 초기화
   */
  const reset = useCallback(() => {
    generationRef.current += 1;
    setIsSendingCode(false);
    setIsVerifying(false);
    setError(null);
    setCodeSent(false);
    confirmationResultRef.current = null;

    // reCAPTCHA 정리
    if (recaptchaVerifierRef.current) {
      try {
        recaptchaVerifierRef.current.clear();
      } catch {
        // 의도적으로 에러 무시: reCAPTCHA clear 실패는 이미 정리된 상태일 수 있음
      }
      recaptchaVerifierRef.current = null;
    }
  }, []);

  /**
   * 에러만 초기화
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // 상태
    isSendingCode,
    isVerifying,
    isLoading: isSendingCode || isVerifying,
    error,
    codeSent,
    isAvailable,

    // 핸들러
    sendVerificationCode,
    confirmCode,
    reset,
    clearError,
  };
}

// #522 — Locale 단일 출처 정합 (15언어 전수). 누락 시 컴파일 에러.
type LocalizedMessage =
  & Record<Language, string>
  & Record<Exclude<Locale, Language>, string>;

/**
 * Firebase 에러 코드를 사용자 친화적 메시지로 변환
 */
const errorMessages: Record<string, LocalizedMessage> = {
  // Firebase Auth 에러
  'auth/invalid-phone-number': {
    ko: '유효하지 않은 전화번호입니다.',
    es: 'El número de teléfono no es válido.',
    en: 'Invalid phone number.',
    pt: 'O número de telefone não é válido.',
    fr: "Le numéro de téléphone n'est pas valide.",
    zh: '电话号码无效。',
    ja: '有効な電話番号ではありません。',
    hi: 'फ़ोन नंबर मान्य नहीं है।',
    de: 'Die Telefonnummer ist ungültig.',
    it: 'Il numero di telefono non è valido.',
    ru: 'Недействительный номер телефона.',
    ar: 'رقم الهاتف غير صالح.',
    vi: 'Số điện thoại không hợp lệ.',
    th: 'หมายเลขโทรศัพท์ไม่ถูกต้อง',
    nl: 'Het telefoonnummer is ongeldig.',
  },
  'auth/missing-phone-number': {
    ko: '전화번호를 입력해주세요.',
    es: 'Por favor, ingresa tu número de teléfono.',
    en: 'Please enter your phone number.',
    pt: 'Insira seu número de telefone.',
    fr: 'Veuillez saisir votre numéro de téléphone.',
    zh: '请输入电话号码。',
    ja: '電話番号を入力してください。',
    hi: 'कृपया अपना फ़ोन नंबर दर्ज करें।',
    de: 'Bitte geben Sie Ihre Telefonnummer ein.',
    it: 'Inserisci il tuo numero di telefono.',
    ru: 'Введите номер телефона.',
    ar: 'يرجى إدخال رقم هاتفك.',
    vi: 'Vui lòng nhập số điện thoại.',
    th: 'กรุณากรอกหมายเลขโทรศัพท์',
    nl: 'Voer uw telefoonnummer in.',
  },
  'auth/quota-exceeded': {
    ko: 'SMS 발송 한도를 초과했습니다. 잠시 후 다시 시도해주세요.',
    es: 'Se ha excedido el límite de SMS. Por favor, inténtalo más tarde.',
    en: 'SMS quota exceeded. Please try again later.',
    pt: 'O limite de SMS foi excedido. Tente novamente mais tarde.',
    fr: 'Le quota de SMS est dépassé. Veuillez réessayer plus tard.',
    zh: '短信发送已达上限，请稍后再试。',
    ja: 'SMSの送信上限を超えました。しばらくしてからもう一度お試しください。',
    hi: 'SMS की सीमा पार हो गई है। कृपया बाद में फिर से प्रयास करें।',
    de: 'Das SMS-Limit wurde überschritten. Bitte versuchen Sie es später erneut.',
    it: 'Limite di SMS superato. Riprova più tardi.',
    ru: 'Превышен лимит SMS. Повторите попытку позже.',
    ar: 'تم تجاوز حد إرسال الرسائل النصية. يرجى المحاولة لاحقًا.',
    vi: 'Đã vượt quá giới hạn gửi SMS. Vui lòng thử lại sau.',
    th: 'เกินจำนวนการส่ง SMS ที่กำหนด กรุณาลองใหม่ภายหลัง',
    nl: 'De sms-limiet is overschreden. Probeer het later opnieuw.',
  },
  'auth/user-disabled': {
    ko: '비활성화된 계정입니다.',
    es: 'Esta cuenta ha sido deshabilitada.',
    en: 'This account has been disabled.',
    pt: 'Esta conta foi desativada.',
    fr: 'Ce compte a été désactivé.',
    zh: '该账户已被禁用。',
    ja: 'このアカウントは無効化されています。',
    hi: 'यह खाता निष्क्रिय कर दिया गया है।',
    de: 'Dieses Konto wurde deaktiviert.',
    it: 'Questo account è stato disabilitato.',
    ru: 'Эта учётная запись отключена.',
    ar: 'تم تعطيل هذا الحساب.',
    vi: 'Tài khoản này đã bị vô hiệu hóa.',
    th: 'บัญชีนี้ถูกปิดใช้งาน',
    nl: 'Dit account is uitgeschakeld.',
  },
  'auth/operation-not-allowed': {
    ko: '전화번호 인증이 비활성화되어 있습니다.',
    es: 'La autenticación por teléfono está deshabilitada.',
    en: 'Phone authentication is disabled.',
    pt: 'A autenticação por telefone está desativada.',
    fr: "L'authentification par téléphone est désactivée.",
    zh: '电话号码验证已停用。',
    ja: '電話番号認証が無効になっています。',
    hi: 'फ़ोन प्रमाणीकरण अक्षम है।',
    de: 'Die Telefonauthentifizierung ist deaktiviert.',
    it: "L'autenticazione tramite telefono è disabilitata.",
    ru: 'Аутентификация по телефону отключена.',
    ar: 'المصادقة عبر الهاتف معطلة.',
    vi: 'Xác thực qua điện thoại đã bị tắt.',
    th: 'การยืนยันตัวตนทางโทรศัพท์ถูกปิดใช้งาน',
    nl: 'Telefoonverificatie is uitgeschakeld.',
  },
  'auth/invalid-verification-code': {
    ko: '인증 코드가 올바르지 않습니다.',
    es: 'El código de verificación es incorrecto.',
    en: 'Invalid verification code.',
    pt: 'O código de verificação está incorreto.',
    fr: 'Le code de vérification est incorrect.',
    zh: '验证码不正确。',
    ja: '認証コードが正しくありません。',
    hi: 'सत्यापन कोड गलत है।',
    de: 'Der Bestätigungscode ist ungültig.',
    it: 'Il codice di verifica non è corretto.',
    ru: 'Неверный код подтверждения.',
    ar: 'رمز التحقق غير صحيح.',
    vi: 'Mã xác minh không đúng.',
    th: 'รหัสยืนยันไม่ถูกต้อง',
    nl: 'De verificatiecode is onjuist.',
  },
  'auth/invalid-verification-id': {
    ko: '인증 세션이 만료되었습니다. 다시 시도해주세요.',
    es: 'La sesión ha expirado. Por favor, inténtalo de nuevo.',
    en: 'Session expired. Please try again.',
    pt: 'A sessão expirou. Tente novamente.',
    fr: 'La session a expiré. Veuillez réessayer.',
    zh: '验证会话已过期，请重试。',
    ja: '認証セッションの有効期限が切れました。もう一度お試しください。',
    hi: 'सत्र समाप्त हो गया है। कृपया फिर से प्रयास करें।',
    de: 'Die Sitzung ist abgelaufen. Bitte versuchen Sie es erneut.',
    it: 'La sessione è scaduta. Riprova.',
    ru: 'Сессия истекла. Повторите попытку.',
    ar: 'انتهت صلاحية الجلسة. يرجى المحاولة مرة أخرى.',
    vi: 'Phiên đã hết hạn. Vui lòng thử lại.',
    th: 'เซสชันหมดอายุ กรุณาลองอีกครั้ง',
    nl: 'De sessie is verlopen. Probeer het opnieuw.',
  },
  'auth/code-expired': {
    ko: '인증 코드가 만료되었습니다. 다시 요청해주세요.',
    es: 'El código ha expirado. Por favor, solicita uno nuevo.',
    en: 'Verification code expired. Please request a new one.',
    pt: 'O código expirou. Solicite um novo.',
    fr: 'Le code a expiré. Veuillez en demander un nouveau.',
    zh: '验证码已过期，请重新获取。',
    ja: '認証コードの有効期限が切れました。再度リクエストしてください。',
    hi: 'कोड की अवधि समाप्त हो गई है। कृपया नया अनुरोध करें।',
    de: 'Der Code ist abgelaufen. Bitte fordern Sie einen neuen an.',
    it: 'Il codice è scaduto. Richiedine uno nuovo.',
    ru: 'Срок действия кода истёк. Запросите новый.',
    ar: 'انتهت صلاحية الرمز. يرجى طلب رمز جديد.',
    vi: 'Mã đã hết hạn. Vui lòng yêu cầu mã mới.',
    th: 'รหัสหมดอายุแล้ว กรุณาขอรหัสใหม่',
    nl: 'De code is verlopen. Vraag een nieuwe aan.',
  },
  'auth/too-many-requests': {
    ko: '너무 많은 요청입니다. 잠시 후 다시 시도해주세요.',
    es: 'Demasiadas solicitudes. Por favor, inténtalo más tarde.',
    en: 'Too many requests. Please try again later.',
    pt: 'Muitas solicitações. Tente novamente mais tarde.',
    fr: 'Trop de requêtes. Veuillez réessayer plus tard.',
    zh: '请求过多，请稍后再试。',
    ja: 'リクエストが多すぎます。しばらくしてからもう一度お試しください。',
    hi: 'बहुत अधिक अनुरोध। कृपया बाद में फिर से प्रयास करें।',
    de: 'Zu viele Anfragen. Bitte versuchen Sie es später erneut.',
    it: 'Troppe richieste. Riprova più tardi.',
    ru: 'Слишком много запросов. Повторите попытку позже.',
    ar: 'طلبات كثيرة جدًا. يرجى المحاولة لاحقًا.',
    vi: 'Quá nhiều yêu cầu. Vui lòng thử lại sau.',
    th: 'มีคำขอมากเกินไป กรุณาลองใหม่ภายหลัง',
    nl: 'Te veel verzoeken. Probeer het later opnieuw.',
  },
  'auth/captcha-check-failed': {
    ko: 'reCAPTCHA 검증에 실패했습니다.',
    es: 'La verificación reCAPTCHA ha fallado.',
    en: 'reCAPTCHA verification failed.',
    pt: 'A verificação reCAPTCHA falhou.',
    fr: 'La vérification reCAPTCHA a échoué.',
    zh: 'reCAPTCHA验证失败。',
    ja: 'reCAPTCHA認証に失敗しました。',
    hi: 'reCAPTCHA सत्यापन विफल हुआ।',
    de: 'Die reCAPTCHA-Überprüfung ist fehlgeschlagen.',
    it: 'La verifica reCAPTCHA non è riuscita.',
    ru: 'Проверка reCAPTCHA не пройдена.',
    ar: 'فشل التحقق من reCAPTCHA.',
    vi: 'Xác minh reCAPTCHA thất bại.',
    th: 'การยืนยัน reCAPTCHA ล้มเหลว',
    nl: 'reCAPTCHA-verificatie mislukt.',
  },
  'auth/network-request-failed': {
    ko: '네트워크 연결을 확인해주세요.',
    es: 'Por favor, verifica tu conexión a internet.',
    en: 'Please check your network connection.',
    pt: 'Verifique sua conexão com a internet.',
    fr: 'Veuillez vérifier votre connexion Internet.',
    zh: '请检查您的网络连接。',
    ja: 'ネットワーク接続をご確認ください。',
    hi: 'कृपया अपना नेटवर्क कनेक्शन जांचें।',
    de: 'Bitte überprüfen Sie Ihre Netzwerkverbindung.',
    it: 'Verifica la tua connessione a internet.',
    ru: 'Проверьте подключение к сети.',
    ar: 'يرجى التحقق من اتصالك بالإنترنت.',
    vi: 'Vui lòng kiểm tra kết nối mạng.',
    th: 'กรุณาตรวจสอบการเชื่อมต่อเครือข่าย',
    nl: 'Controleer uw netwerkverbinding.',
  },
  'auth/internal-error': {
    ko: '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
    es: 'Error del servidor. Por favor, inténtalo más tarde.',
    en: 'Server error. Please try again later.',
    pt: 'Erro no servidor. Tente novamente mais tarde.',
    fr: 'Erreur du serveur. Veuillez réessayer plus tard.',
    zh: '服务器错误，请稍后再试。',
    ja: 'サーバーエラーが発生しました。しばらくしてからもう一度お試しください。',
    hi: 'सर्वर त्रुटि। कृपया बाद में फिर से प्रयास करें।',
    de: 'Serverfehler. Bitte versuchen Sie es später erneut.',
    it: 'Errore del server. Riprova più tardi.',
    ru: 'Ошибка сервера. Повторите попытку позже.',
    ar: 'خطأ في الخادم. يرجى المحاولة لاحقًا.',
    vi: 'Lỗi máy chủ. Vui lòng thử lại sau.',
    th: 'เกิดข้อผิดพลาดของเซิร์ฟเวอร์ กรุณาลองใหม่ภายหลัง',
    nl: 'Serverfout. Probeer het later opnieuw.',
  },

  // 커스텀 에러
  FIREBASE_NOT_INITIALIZED: {
    ko: 'Firebase가 초기화되지 않았습니다.',
    es: 'Firebase no está inicializado.',
    en: 'Firebase is not initialized.',
    pt: 'O Firebase não está inicializado.',
    fr: "Firebase n'est pas initialisé.",
    zh: 'Firebase未初始化。',
    ja: 'Firebaseが初期化されていません。',
    hi: 'Firebase प्रारंभ नहीं हुआ है।',
    de: 'Firebase ist nicht initialisiert.',
    it: 'Firebase non è inizializzato.',
    ru: 'Firebase не инициализирован.',
    ar: 'لم تتم تهيئة Firebase.',
    vi: 'Firebase chưa được khởi tạo.',
    th: 'Firebase ยังไม่ได้เริ่มต้น',
    nl: 'Firebase is niet geïnitialiseerd.',
  },
  RECAPTCHA_SETUP_FAILED: {
    ko: 'reCAPTCHA 설정에 실패했습니다.',
    es: 'Error al configurar reCAPTCHA.',
    en: 'Failed to setup reCAPTCHA.',
    pt: 'Falha ao configurar o reCAPTCHA.',
    fr: 'Échec de la configuration du reCAPTCHA.',
    zh: 'reCAPTCHA设置失败。',
    ja: 'reCAPTCHAの設定に失敗しました。',
    hi: 'reCAPTCHA सेटअप विफल हुआ।',
    de: 'Einrichtung von reCAPTCHA fehlgeschlagen.',
    it: 'Configurazione di reCAPTCHA non riuscita.',
    ru: 'Не удалось настроить reCAPTCHA.',
    ar: 'فشل في إعداد reCAPTCHA.',
    vi: 'Thiết lập reCAPTCHA thất bại.',
    th: 'ตั้งค่า reCAPTCHA ไม่สำเร็จ',
    nl: 'Instellen van reCAPTCHA mislukt.',
  },
  RECAPTCHA_EXPIRED: {
    ko: 'reCAPTCHA가 만료되었습니다. 다시 시도해주세요.',
    es: 'reCAPTCHA ha expirado. Por favor, inténtalo de nuevo.',
    en: 'reCAPTCHA expired. Please try again.',
    pt: 'O reCAPTCHA expirou. Tente novamente.',
    fr: 'Le reCAPTCHA a expiré. Veuillez réessayer.',
    zh: 'reCAPTCHA已过期，请重试。',
    ja: 'reCAPTCHAの有効期限が切れました。もう一度お試しください。',
    hi: 'reCAPTCHA समाप्त हो गया है। कृपया फिर से प्रयास करें।',
    de: 'reCAPTCHA ist abgelaufen. Bitte versuchen Sie es erneut.',
    it: 'reCAPTCHA è scaduto. Riprova.',
    ru: 'Срок действия reCAPTCHA истёк. Повторите попытку.',
    ar: 'انتهت صلاحية reCAPTCHA. يرجى المحاولة مرة أخرى.',
    vi: 'reCAPTCHA đã hết hạn. Vui lòng thử lại.',
    th: 'reCAPTCHA หมดอายุ กรุณาลองอีกครั้ง',
    nl: 'reCAPTCHA is verlopen. Probeer het opnieuw.',
  },
  INVALID_PHONE_NUMBER: {
    ko: '유효한 전화번호를 입력해주세요.',
    es: 'Por favor, ingresa un número de teléfono válido.',
    en: 'Please enter a valid phone number.',
    pt: 'Insira um número de telefone válido.',
    fr: 'Veuillez saisir un numéro de téléphone valide.',
    zh: '请输入有效的电话号码。',
    ja: '有効な電話番号を入力してください。',
    hi: 'कृपया एक मान्य फ़ोन नंबर दर्ज करें।',
    de: 'Bitte geben Sie eine gültige Telefonnummer ein.',
    it: 'Inserisci un numero di telefono valido.',
    ru: 'Введите действительный номер телефона.',
    ar: 'يرجى إدخال رقم هاتف صالح.',
    vi: 'Vui lòng nhập số điện thoại hợp lệ.',
    th: 'กรุณากรอกหมายเลขโทรศัพท์ที่ถูกต้อง',
    nl: 'Voer een geldig telefoonnummer in.',
  },
  NO_CONFIRMATION_RESULT: {
    ko: '먼저 인증 코드를 요청해주세요.',
    es: 'Por favor, solicita primero el código de verificación.',
    en: 'Please request a verification code first.',
    pt: 'Solicite primeiro o código de verificação.',
    fr: "Veuillez d'abord demander le code de vérification.",
    zh: '请先获取验证码。',
    ja: '先に認証コードをリクエストしてください。',
    hi: 'कृपया पहले सत्यापन कोड का अनुरोध करें।',
    de: 'Bitte fordern Sie zuerst einen Bestätigungscode an.',
    it: 'Richiedi prima il codice di verifica.',
    ru: 'Сначала запросите код подтверждения.',
    ar: 'يرجى طلب رمز التحقق أولًا.',
    vi: 'Vui lòng yêu cầu mã xác minh trước.',
    th: 'กรุณาขอรหัสยืนยันก่อน',
    nl: 'Vraag eerst een verificatiecode aan.',
  },
  INVALID_CODE_FORMAT: {
    ko: '6자리 인증 코드를 입력해주세요.',
    es: 'Por favor, ingresa el código de 6 dígitos.',
    en: 'Please enter the 6-digit code.',
    pt: 'Insira o código de 6 dígitos.',
    fr: 'Veuillez saisir le code à 6 chiffres.',
    zh: '请输入6位验证码。',
    ja: '6桁の認証コードを入力してください。',
    hi: 'कृपया 6 अंकों का कोड दर्ज करें।',
    de: 'Bitte geben Sie den 6-stelligen Code ein.',
    it: 'Inserisci il codice a 6 cifre.',
    ru: 'Введите 6-значный код.',
    ar: 'يرجى إدخال الرمز المكوّن من 6 أرقام.',
    vi: 'Vui lòng nhập mã 6 chữ số.',
    th: 'กรุณากรอกรหัส 6 หลัก',
    nl: 'Voer de 6-cijferige code in.',
  },
  BACKEND_LOGIN_FAILED: {
    ko: '로그인에 실패했습니다. 다시 시도해주세요.',
    es: 'Error al iniciar sesión. Por favor, inténtalo de nuevo.',
    en: 'Login failed. Please try again.',
    pt: 'Falha ao fazer login. Tente novamente.',
    fr: 'Échec de la connexion. Veuillez réessayer.',
    zh: '登录失败，请重试。',
    ja: 'ログインに失敗しました。もう一度お試しください。',
    hi: 'लॉगिन विफल। कृपया फिर से प्रयास करें।',
    de: 'Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.',
    it: 'Accesso non riuscito. Riprova.',
    ru: 'Не удалось войти. Повторите попытку.',
    ar: 'فشل تسجيل الدخول. يرجى المحاولة مرة أخرى.',
    vi: 'Đăng nhập thất bại. Vui lòng thử lại.',
    th: 'เข้าสู่ระบบไม่สำเร็จ กรุณาลองอีกครั้ง',
    nl: 'Inloggen mislukt. Probeer het opnieuw.',
  },
  UNKNOWN_ERROR: {
    ko: '오류가 발생했습니다. 다시 시도해주세요.',
    es: 'Ocurrió un error. Por favor, inténtalo de nuevo.',
    en: 'An error occurred. Please try again.',
    pt: 'Ocorreu um erro. Tente novamente.',
    fr: 'Une erreur est survenue. Veuillez réessayer.',
    zh: '发生错误，请重试。',
    ja: 'エラーが発生しました。もう一度お試しください。',
    hi: 'एक त्रुटि हुई। कृपया फिर से प्रयास करें।',
    de: 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.',
    it: 'Si è verificato un errore. Riprova.',
    ru: 'Произошла ошибка. Повторите попытку.',
    ar: 'حدث خطأ. يرجى المحاولة مرة أخرى.',
    vi: 'Đã xảy ra lỗi. Vui lòng thử lại.',
    th: 'เกิดข้อผิดพลาด กรุณาลองอีกครั้ง',
    nl: 'Er is een fout opgetreden. Probeer het opnieuw.',
  },
};

/**
 * 에러 코드에서 메시지 조회 (#522: Locale 확장)
 */
function getErrorMessage(errorCode: string, language: Locale): string {
  const messages = errorMessages[errorCode];
  if (messages) {
    return messages[language] || messages.es;
  }

  // 알 수 없는 에러
  const defaultMessages = errorMessages.UNKNOWN_ERROR;
  return defaultMessages[language] || defaultMessages.es;
}
